import { useEffect, useRef, useState } from "react";
import { EllipsisHorizontal } from "../../../components/icons/ellipsis-horizontal";
import { PasteOutlined } from "../../../components/icons/paste-outlined";
import { CopyOutlined } from "../../../components/icons/copy-outlined";
import { ArrowRightCircleOutlined } from "../../../components/icons/arrow-right-circle-outlined";
import { TrashOutlined } from "../../../components/icons/trash-outlined";

interface Props {
  id: string;
  absentTime: number;
  onImportFromPreviousDate: (() => void) | null;
  onCopy: (() => void) | null;
  onPaste: (() => void) | null;
  onDelete: (() => void) | null;
}

export default function DateActions(props: Props) {
  const {
    id,
    absentTime,
    onImportFromPreviousDate,
    onCopy,
    onPaste,
    onDelete,
  } = props;

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        dropdownButtonRef.current &&
        !dropdownButtonRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div
        key={id}
        ref={dropdownButtonRef}
        id={`dateActionsDropdown-${id}`}
        onClick={(e) => {
          e.preventDefault();
          setOpen(!open);
        }}
        className="hover:bg-gray-200 p-0.5 cursor-pointer rounded-md"
      >
        <EllipsisHorizontal className="h-5 w-5 color-common-black" />
      </div>
      {open && (
        <div
          ref={dropdownRef}
          id={`dateActionsMenu-${id}`}
          className="min-w-48 p-2 fade-in absolute right-0 top-5 mt-2 z-50 bg-background-paper border border-divider divide-y rounded-lg shadow-lg text-sm"
        >
          <ul aria-labelledby="dropdownInformationButton rounded-lg">
            <li className="flex w-full justify-between items-center p-2 italic text-text-title">
              <label>Off hours</label>
              <label
                className={`${
                  absentTime === 8 ? "text-error-main" : "text-text-title"
                }`}
              >
                {absentTime}
              </label>
            </li>
            <hr className="bg-divider my-2" />
            <li
              className={`flex items-center p-2 rounded-lg cursor-pointer space-x-2 ${
                onImportFromPreviousDate
                  ? "text-text-primary hover:bg-gray-100"
                  : "text-action-disabled"
              }`}
              onClick={() => {
                if (onImportFromPreviousDate) {
                  setOpen(false);
                  onImportFromPreviousDate();
                }
              }}
            >
              <ArrowRightCircleOutlined className="h-4 w-4" />
              <label className="cursor-pointer">
                Import from Previous Date
              </label>
            </li>
            <li
              className={`flex items-center p-2 rounded-lg cursor-pointer space-x-2 ${
                onCopy
                  ? "text-text-primary hover:bg-gray-100"
                  : "text-action-disabled"
              }`}
              onClick={() => {
                if (onCopy) {
                  setOpen(false);
                  onCopy();
                }
              }}
            >
              <CopyOutlined className="h-4 w-4" />
              <label className="cursor-pointer">Copy</label>
            </li>
            <li
              className={`flex items-center p-2 rounded-lg cursor-pointer space-x-2 ${
                onPaste
                  ? "text-text-primary hover:bg-gray-100"
                  : "text-action-disabled"
              }`}
              onClick={() => {
                if (onPaste) {
                  setOpen(false);
                  onPaste();
                }
              }}
            >
              <PasteOutlined className="h-4 w-4" />
              <label className="cursor-pointer">Paste</label>
            </li>
            <li
              className={`flex items-center p-2 rounded-lg cursor-pointer space-x-2 ${
                onDelete
                  ? "text-error-main hover:bg-gray-100"
                  : "text-action-disabled"
              }`}
              onClick={() => {
                if (onDelete) {
                  setOpen(false);
                  onDelete();
                }
              }}
            >
              <TrashOutlined className="h-4 w-4" />
              <label className="cursor-pointer">Delete All</label>
            </li>
          </ul>
        </div>
      )}
    </>
  );
}
